import { Grid, Box, rem, Flex, Switch, ThemeIcon, Divider, Text, Stack, Chip } from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons-react";
import React, { useEffect, useState } from "react";
import {
  getDecimalFromLogmar,
  getDisplayValuesFromLogmar,
  getSnellenFromLogmar,
  getVisualAcuityLabelByNotation,
  VisualAcuityNotation,
} from "@utils/test-specific-utils/VisualAcuityUtils";
import { useTranslation } from "react-i18next";
import { STATUS_COLOR } from "@constants/resultColors";

interface VAcuityResultLineProps {
  handleSwitchChange?: (rowIndex: number, letterIndex: number, checked: boolean) => void;
  row: number;
  letters: string[];
  value?: number;
  isActive: boolean;
  rowResults: number[];
  isResultLine?: boolean;
  handleSetAll?: (rowIndex: number, value: number) => void;
  handleSeenAll?: (rowIndex: number) => void;
  hideInfo?: boolean;
  notation?: VisualAcuityNotation;
}

export default function VAcuityResultLine(props: VAcuityResultLineProps) {
  const [checked, setChecked] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (!props.handleSetAll) return;
    if (checked) {
      props.handleSetAll(props.row, 1);
    } else if (props.rowResults) {
      const isAllFalse = Object.values(props.rowResults).every((v) => v === 0);
      const isAllTrue = Object.values(props.rowResults).every((v) => v === 1);
      if (!isAllFalse && !isAllTrue) return;
      props.handleSetAll(props.row, 0);
    }
  }, [checked]);

  useEffect(() => {
    if (props.rowResults) {
      setChecked(Object.values(props.rowResults).every((v) => v === 1));
    }
  }, [props.rowResults]);

  const getLetter = (
    letter,
    value: number | undefined,
    isActive,
    letterIndex: number,
    rowIndex: number,
  ) => {
    const scale = value ? value : 1;
    return (
      <Grid.Col
        key={`letter-${rowIndex}-${letterIndex}-${letter}`}
        span={2}
        p={0}
        pl={letterIndex === 0 ? "lg" : undefined}
        pr={letterIndex === props.letters.length - 1 ? "lg" : undefined}
      >
        <Box>
          <Text ta={"center"} fz={rem(scale * 100)} style={{ lineHeight: "1.2" }}>
            {letter}
          </Text>
        </Box>
        <Flex justify={"center"} mb={"md"}>
          {isActive ? (
            <Switch
              onChange={(event) =>
                props.handleSwitchChange(rowIndex, letterIndex, event.currentTarget.checked)
              }
              checked={props.rowResults ? props.rowResults[letterIndex] === 1 : false}
              color={STATUS_COLOR.good}
              size="lg"
              onLabel={<IconCheck />}
              offLabel={<IconX />}
            />
          ) : (
            getResultIcon(letterIndex)
          )}
        </Flex>
      </Grid.Col>
    );
  };

  const getResultIcon = (letterIndex) => {
    return props.rowResults ? (
      props.rowResults[letterIndex] === 1 ? (
        <ThemeIcon size="sm" radius="lg" color={STATUS_COLOR.good}>
          <IconCheck />
        </ThemeIcon>
      ) : (
        <ThemeIcon size="sm" radius="lg" color={STATUS_COLOR.bad}>
          <IconX />
        </ThemeIcon>
      )
    ) : (
      <ThemeIcon size="sm" radius="lg" color={STATUS_COLOR.bad}>
        <IconX />
      </ThemeIcon>
    );
  };

  const redLine = (isActive, value?, isRight?) => {
    const scale = value ? value : 1;
    return (
      <Grid.Col span={2} p={0} h={"100%"}>
        <Box h={rem(scale * 100)}>
          <Stack justify="flex-end" h={"50%"}>
            {isActive && <Divider size="xl" color={"red.7"} />}
          </Stack>
          {isActive && isRight && (
            <Stack justify="center" align="center" h={"100%"}>
              <Chip
                checked={checked}
                onChange={(isChecked) => {
                  if (isChecked && !checked) {
                    props.handleSeenAll?.(props.row);
                  }
                  setChecked(isChecked);
                }}
                color={STATUS_COLOR.good}
                size="md"
              >
                {t("TestProgress.seenAll")}
              </Chip>
            </Stack>
          )}
        </Box>
      </Grid.Col>
    );
  };

  const infoText = (text) => {
    return (
      <Grid.Col span={1} p={0}>
        <Text ta={"center"} fz={"md"} c={"gray.6"}>
          {text}
        </Text>
      </Grid.Col>
    );
  };

  const spacer = () => {
    return <Grid.Col span={1} p={0}></Grid.Col>;
  };

  const letterRow = (row) => {
    const value = props.value ?? 1;
    const displayValues = getDisplayValuesFromLogmar(value);
    const leftLabel = getSnellenFromLogmar(value);
    const rightLabel = props.notation
      ? getVisualAcuityLabelByNotation(displayValues, props.notation)
      : (getDecimalFromLogmar(value) ?? String(value));
    return (
      <>
        {redLine(props.isActive || props.isResultLine, value, false)}
        {props.hideInfo ? spacer() : infoText(leftLabel)}
        {props.letters.map((letter, letterIndex) =>
          getLetter(letter, value, props.isActive, letterIndex, row),
        )}
        {props.hideInfo ? spacer() : infoText(rightLabel)}
        {redLine(props.isActive || props.isResultLine, value, !props.isResultLine)}
      </>
    );
  };

  return letterRow(props.row);
}
